import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";import { toast } from "sonner";
import { Fish, ArrowRight, Eye, EyeSlash, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth.jsx";
import { isMiniapp } from '@/lib/miniappSession';

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Selamat datang kembali!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell min-h-screen flex items-center justify-center px-4 py-8 sm:px-6">
      <div className="w-full max-w-5xl grid lg:grid-cols-[1fr_0.92fr] overflow-hidden rounded-[28px] login-frame">
        <div className="hidden lg:flex flex-col justify-between p-12 login-story">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-pink-500 flex items-center justify-center">
              <Fish size={23} weight="fill" className="text-white" />
            </div>
            <span className="font-heading font-bold text-lg">FISH IT</span>
          </div>
          <div>
            <div className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-pink-300">Panel kendali</div>
            <h2 className="font-heading text-4xl font-bold leading-tight">Semua akun, lebih mudah dipantau.</h2>
            <p className="mt-5 max-w-sm text-base leading-relaxed text-slate-300">Lihat status bot, kelola Telegram, dan tangani hal penting dalam satu tempat.</p>
            <div className="mt-9 space-y-4 text-sm text-slate-200">
              <div className="flex items-center gap-3"><CheckCircle size={20} className="text-emerald-300" /> Status akun mudah dibaca</div>
              <div className="flex items-center gap-3"><CheckCircle size={20} className="text-emerald-300" /> Kontrol cepat tanpa mencari menu</div>
            </div>
          </div>
          <div className="text-xs text-slate-400">FISH IT / AUTOPILOT</div>
        </div>

        <div className="login-form-panel p-6 sm:p-10 lg:p-12">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-10" data-testid="login-back-home">
            <div className="w-9 h-9 rounded-lg bg-pink-500 flex items-center justify-center">
              <Fish size={20} weight="fill" className="text-white" />
            </div>
            <span className="font-heading font-bold text-sm tracking-tight">FISH IT / AUTOPILOT</span>
          </Link>
          <div className="mb-8">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-pink-300 mb-3">Selamat datang</div>
            <h1 className="font-heading text-3xl font-bold mb-2">Masuk ke panel</h1>
            <p className="text-slate-400 text-sm leading-relaxed">Pantau dan kelola akun Fish It Anda.</p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            {isMiniapp() && <p className="text-xs leading-relaxed text-slate-300" data-testid="miniapp-login-notice">
              Dengan masuk, Telegram ini terhubung ke akun FishIt Anda untuk notifikasi dan kontrol bot.
              Pastikan Anda memakai Telegram dan akun FishIt milik sendiri.
            </p>}
            <div>
              <Label htmlFor="email" className="text-slate-300 mb-2 block">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="login-field border-white/15 focus-visible:ring-pink-400 h-12 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300 mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="login-field border-white/15 focus-visible:ring-pink-400 h-12 rounded-xl pr-12"
                />
                <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"} aria-pressed={showPassword} className="absolute right-1 top-1 flex h-10 w-10 items-center justify-center rounded-lg text-slate-400 hover:text-white focus-visible:ring-2 focus-visible:ring-pink-400">
                  {showPassword ? <EyeSlash size={19} /> : <Eye size={19} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-pink-400 hover:bg-pink-300 text-slate-950 font-bold h-12 rounded-xl"
            >
              {loading ? "Memproses..." : "Masuk"} <ArrowRight size={16} className="ml-1" />
            </Button>
          </form>

          <p className="mt-7 text-xs text-slate-400 text-center leading-relaxed" data-testid="login-forgot-hint">
            Butuh akun operator? Hubungi admin. Lupa password? Minta admin mereset.
          </p>
        </div>
      </div>
    </div>
  );
}
